import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { environment } from 'src/environments/environment';

export interface PropertyInsuranceAddress {
  id: number;
  address_name: string;
}

export interface PropertyInsurance {
  id: number;
  address: PropertyInsuranceAddress;
  provider_name: string;
  policy_number: string;
  coverage_type: string;
  coverage_amount: number;
  annual_premium_amount: number;
  deductible_amount: number;
  effective_date: string;
  expiration_date: string;
  auto_renew: boolean;
  agent_name: string;
  agent_phone: string;
  ai_agent_enabled: boolean;
  created_at: string;
  updated_at: string;
  /** Where the policy came from — `steadily` rows are upserted by the nightly sync (gotidy PR #8539). */
  source?: 'manual' | 'steadily';
  steadily_policy_id?: string | null;
  steadily_status?: string | null;
}

/**
 * Must stay in sync with `AddressInsurance::COVERAGE_TYPES` in gotidy — the create/update
 * endpoints declare `values: ::AddressInsurance::COVERAGE_TYPES.keys`, so anything else is
 * rejected with a 400 before it reaches the model. Adding a type here needs a gotidy change.
 */
export const COVERAGE_TYPE_LABELS: Record<string, string> = {
  short_term_rental: 'Short-Term Rental',
  landlord_rental_property: 'Landlord / Rental Property',
  flood: 'Flood',
  umbrella: 'Umbrella',
};

@Injectable({
  providedIn: 'root'
})
export class PropertyInsuranceService {

  private http = inject(HttpClient);

  async getPropertyInsurances(): Promise<PropertyInsurance[]> {
    return firstValueFrom(
      this.http.get<PropertyInsurance[]>(`${environment.api_url}api/v1/customer/address-insurances`)
    );
  }

  async createPropertyInsurance(insurance: Partial<PropertyInsurance>): Promise<PropertyInsurance> {
    return firstValueFrom(
      this.http.post<PropertyInsurance>(`${environment.api_url}api/v1/customer/address-insurances`, insurance)
    );
  }

  async updatePropertyInsurance(id: number, insurance: Partial<PropertyInsurance>): Promise<PropertyInsurance> {
    return firstValueFrom(
      this.http.put<PropertyInsurance>(`${environment.api_url}api/v1/customer/address-insurances/${id}`, insurance)
    );
  }

  async deletePropertyInsurance(id: number): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(`${environment.api_url}api/v1/customer/address-insurances/${id}`)
    );
  }
}
